import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import querystring from 'query-string'
import { listingsUrlGenerator } from '@pcln/fly-fe-common-utils'
import { getPkgRguid } from '../../utils/getPkgRguid'
import { packageBasePath } from '../../common/constants/constants'
import { getNegotiatorapp } from '@mfs/client/utils/getNegotiatorapp'

// This will be cleaned up when baggage SETI is declared
export function isSpiritOrFrontier(airlines) {
  return airlines.some((airline) => {
    return airline.code === 'NK' || airline.code === 'F9'
  })
}

// Build checkout querystring from new seats card
export function buildQueryString(data) {
  const params = get(data, 'params', {})

  const queryStringParams = {
    'group-id': params['group-id'] || get(data, 'itineraryDetails.itineraryReference.groupId', ''),
    'ref-id': params['ref-id'] || get(data, 'itineraryDetails.itineraryReference.refId', ''),
    'ref-key': get(data, 'itineraryDetails.itineraryReference.refKey', ''),
    'num-adults': params['num-adults'],
    'num-children': params['num-children'],
    'num-infants': params['num-infants'],
    'itinerary-type-code': data.itineraryType,
    token: data.token,
    type: params.type || 'rm',
    'passport-required': data.passportRequired,
    'nationality-required': data.nationalityRequired,
    'hide-paid-seats': data.hidePaidSeats,
    'price-key': data.priceKey,
    'item-key': data.itemKey,
    'checkout-obj-key': data.checkoutObjKey,
    'cabin-class': data.cabinClass,
    'seat-selection': data.seatsEligiblility,
    'baggage-selection': data.paidBagsEligiblility,
  }

  if (data.isDeepLink) {
    queryStringParams['r-id'] = params.refid ? params.refid : params['ref-id']
  }

  if (!(data.priceKey && data.itemKey)) {
    queryStringParams['seat-selection'] = true
  }

  if (params.seti) {
    queryStringParams.seti = params.seti
  }

  if (params['seti-default']) {
    queryStringParams['seti-default'] = params['seti-default']
  }

  if (params.refid) {
    queryStringParams.refid = params.refid
  }

  if (params.refclickid) {
    queryStringParams.refclickid = params.refclickid
  }

  if (data.workflowId) {
    queryStringParams['workflow-id'] = data.workflowId
  }

  const negotiatorapp = getNegotiatorapp()
  if (negotiatorapp && data.fromPackages) {
    queryStringParams.negotiatorapp = negotiatorapp
  }

  const query = querystring.stringify(queryStringParams)
  const fareBrandNames = encodeURIComponent(data.fareBrandNames)
  const UCParams =
    data.directToUC && !data.fromPackages ? `&direct-to-uc=true&fare-brand-names=${fareBrandNames}` : ''
  const pkgRguid = getPkgRguid()
  const pkgParams = data.fromPackages
    ? `&cid=${data.cid}&origin=pkg${pkgRguid ? `&pkg-rguid=${pkgRguid}` : ''}`
    : ''
  return `${query}${UCParams}${pkgParams}`
}

export const getPkgUrlFromParams = ({
  params,
  isWhiteLabelForceDetailsEnabled = false,
  basePathToUse = '',
}) => {
  let index = params['component-index']
  const isFromItinerary = params['pkg-return'] === 'iti'
  const id = params['cart-key']
  const route = basePathToUse ? '' : isFromItinerary ? 'itinerary' : 'flights'
  const redirectParams = {
    id,
    'pkg-rguid': getPkgRguid(),
  }

  // Related to https://priceline.atlassian.net/browse/PPN-63732:
  // Prevents a bug in an edge case unique to Spirit Vacations, as they force all users through flight details.
  // This can be removed once all Package traffic migrates to m-fly-search flight listings.
  const parsedIndex = parseInt(index)
  if (isWhiteLabelForceDetailsEnabled && parsedIndex > 0) {
    index = `${parsedIndex - 1}`
  }

  if (!isFromItinerary && index) {
    redirectParams.index = index
  }

  if (params['usp-cache-key']) {
    redirectParams.uspCacheKey = params['usp-cache-key']
  }

  if (params['basket-key']) {
    redirectParams.basketKey = params['basket-key']
  }
  if (params['message']) {
    redirectParams.message = params['message']
  }

  if (params['redirect-source']) {
    redirectParams['redirect-source'] = params['redirect-source']
  }

  return `${basePathToUse || packageBasePath}/${route}?${querystring.stringify(redirectParams)}`
}

export const getPackageListingsUrlFromParams = ({ params }) => {
  const { 'usp-cache-key': uspCacheKey, 'cart-key': cartKey, 'offer-token': offerToken, childAges } = params

  let searchUrl = listingsUrlGenerator({
    searchRequest: { params },
    delimiter: window.location.href.includes('/details') ? 'details' : '?',
  })

  if (uspCacheKey) {
    searchUrl = `${searchUrl}&usp-cache-key=${encodeURIComponent(uspCacheKey)}`
  }
  if (cartKey) {
    searchUrl = `${searchUrl}&cart-key=${encodeURIComponent(cartKey)}`
  }
  if (offerToken) {
    searchUrl = `${searchUrl}&offer-token=${encodeURIComponent(offerToken)}`
  }
  if (childAges) {
    searchUrl = `${searchUrl}&childAges=${childAges}`
  }
  return searchUrl
}

export function shouldReverseCoreAttribute(slices) {
  return slices.map((slice) => {
    slice.fareBands = slice.fareBrands?.map((fareBrand) => {
      if (fareBrand?.airline === 'DL') {
        fareBrand.coreFBAncillaries = fareBrand?.coreFBAncillaries?.reverse()
      }
      return fareBrand
    })
    return slice
  })
}

function getSelectedFares({ defaultBrandsByKeys, selectionsBySliceId = {} }) {
  const selectedFares = []
  const selections = selectionsBySliceId
  for (const sliceId in selections) {
    if (!selectedFares.includes(selections[sliceId])) {
      selectedFares.push(selections[sliceId])
    }
  }
  const isFareBrandUpgrade = !isEqual(defaultBrandsByKeys, selections)
  return {
    selectedFares,
    isFareBrandUpgrade,
  }
}

export function getDefaultBrandsByKeys({ defaultBrandsBySliceIdArray }) {
  return defaultBrandsBySliceIdArray.map((brandBySliceId) => brandBySliceId.value)
}

export function getSelectedFareBrands({ defaultBrandsByKeys, selectionsBySliceId, fareBrandsInfo }) {
  let selectedFareBrands = []

  const selectedFaresObject = getSelectedFares({
    defaultBrandsByKeys,
    selectionsBySliceId,
  })
  const { selectedFares, isFareBrandUpgrade } = selectedFaresObject

  const fareFamilies = fareBrandsInfo?.fareBrandsByKey || []

  if (isFareBrandUpgrade) {
    selectedFareBrands = fareFamilies.filter((fareFamily) => selectedFares.includes(fareFamily.key))
  }

  return {
    isFareBrandUpgrade,
    selectedFareBrands,
  }
}
